import {
    Component,
    Output,
    Input,
    EventEmitter,
    ViewChild,
    ElementRef,
    AfterViewInit,
    OnDestroy,
    inject,
    ChangeDetectionStrategy,
} from '@angular/core';
import {
    DxTreeViewComponent,
    DxTreeViewTypes,
} from 'devextreme-angular/ui/tree-view';
import { navigation } from '../../../app-navigation';
import { trimStart as _trimStart } from 'lodash';
import * as events from 'devextreme/events';
import { Subscription, interval } from 'rxjs';
import { Router } from '@angular/router';

@Component({
    selector: 'app-side-navigation-menu',
    templateUrl: './side-navigation-menu.component.html',
    styleUrls: ['./side-navigation-menu.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [DxTreeViewComponent],
})
export class SideNavigationMenuComponent implements AfterViewInit, OnDestroy {
    private elementRef = inject(ElementRef);
    private router = inject(Router);

    @ViewChild(DxTreeViewComponent, { static: true })
    menu!: DxTreeViewComponent;

    @Output()
    selectedItemChanged = new EventEmitter<DxTreeViewTypes.ItemClickEvent>();

    @Output()
    openMenu = new EventEmitter<any>();

    // The bound value (parent's selectedRoute) only signals that the route
    // changed; the highlight logic reads the router URL directly.
    @Input()
    set selectedItem(_value: string) {
        if (!this.menu.instance) {
            return;
        }
        this.highlightMenuItem();
    }

    private _items!: Record<string, unknown>[];
    get items() {
        if (!this._items) {
            this._items = navigation.map((item) => {
                if (item.path && !/^\//.test(item.path)) {
                    item.path = `/${item.path}`;
                }
                return { ...item, expanded: !this._compactMode };
            });
        }

        return this._items;
    }

    private _compactMode = false;
    @Input()
    get compactMode() {
        return this._compactMode;
    }
    set compactMode(val) {
        this._compactMode = val;

        if (!this.menu.instance) {
            return;
        }

        if (val) {
            this.menu.instance.collapseAll();
        } else {
            this.expandSelectedItem();
        }
    }

    private intervalSubscriber?: Subscription;

    onItemClick(event: DxTreeViewTypes.ItemClickEvent) {
        this.subscribeHighlightMenu();
        this.selectedItemChanged.emit(event);
    }

    ngAfterViewInit() {
        events.on(this.elementRef.nativeElement, 'dxclick', (e: Event) => {
            this.openMenu.next(e);
        });
        this.subscribeHighlightMenu();
    }

    ngOnDestroy() {
        events.off(this.elementRef.nativeElement, 'dxclick');
        this.intervalSubscriber?.unsubscribe();
    }

    private highlightMenuItem(): boolean {
        if (!this.menu.instance) {
            return false;
        }
        // Menu keys are plain paths — ignore query params and fragments.
        const url = this.router.url.split(/[?#]/)[0];
        if (!url) {
            return false;
        }
        // Top-level items keep a leading slash in their path, nested
        // items don't, so try matching the URL both ways.
        const matched =
            this.menu.instance.selectItem(url) ||
            this.menu.instance.selectItem(_trimStart(url, '/'));
        if (!matched) {
            this.menu.instance.unselectAll();
        }
        return matched;
    }

    private expandSelectedItem() {
        // The selection holds the key in whichever form matched; expandItem
        // on a hidden node also re-expands its collapsed ancestors.
        const [key] = this.menu.instance.getSelectedNodeKeys();
        if (key !== undefined) {
            this.menu.instance.expandItem(key);
        }
    }

    private subscribeHighlightMenu() {
        this.intervalSubscriber?.unsubscribe();
        // Poll only until the tree view is ready and the current route is
        // highlighted; route changes after that arrive via the selectedItem
        // input, so give up once the widget had ample time to render.
        let attempts = 0;
        this.intervalSubscriber = interval(50).subscribe(() => {
            attempts += 1;
            if (this.highlightMenuItem() || attempts >= 40) {
                this.intervalSubscriber?.unsubscribe();
            }
        });
    }
}
